import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import { useImagesLoaded } from '../../hooks/useImagesLoaded';
import { useShareablesTemplateRoot } from '../../hooks/useShareablesTemplateRoot';
import { COLOR_BLACK } from '../colors';
import { addCommas, getMonthName, numAbbreviate } from '../../utils';
import { skins, LAYOUT_CONSTANTS } from './skins';
import lodashGet from 'lodash/get';
import StatsHighlight from './StatsHighlight';
import StatsGrid from './StatsGrid';
import StatsStack from './StatsStack';
import StatsColumns from './StatsColumns';
import StatsGridLines from './StatsGridLines';
import { IntlProvider, useFormatMessage } from 'react-intl-hooks';
import lang_en from './translations/en.json';
import lang_es from './translations/es.json';
import { CAVEAT, COURIER_PRIME } from '../fonts';

const languages = {
    en: lang_en,
    es: lang_es,
};


const DEFAULT_SKIN = 'notebook';
const DEFAULT_LAYOUT = 'highlight';

const useStyles = makeStyles((theme)=> ({
    root: {
        fontFamily: ['"LL Circular Pro"', 'Arial', 'Helvetica', 'sans-serif'],
        color: COLOR_BLACK,
    },
    wrap: {
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        boxSizing: 'border-box',
        textAlign: 'center',
        fontSize: '34px',
        lineHeight: '40px'
    },
    area: {
        fontSize: '48px',
        lineHeight: '68px',
        fontWeight: 500,
    },
    watermark: {
        position: 'absolute',
        left: '50%',
        bottom: '30%',
        zIndex: 9999,

        '& img': {
            position: 'relative',
            left: '-50%',
        },
    }
}));

function StatsComponent({ layout, ...restProps }) {
    switch (layout) {
        case 'grid': return <StatsGrid {...restProps} />;
        case 'stack': return <StatsStack {...restProps} />;
        case 'columns': return <StatsColumns {...restProps} />;
        case 'gridlines': return <StatsGridLines {...restProps} />;
        default: return <StatsHighlight {...restProps} />;
    }
}

function Title({ skin, month, area }) {
    const t = useFormatMessage();
    const classes = useStyles();
    const {
        AREA_FONT_SIZE,
        AREA_LINE_HEIGHT,
        AREA_FONT_WEIGHT,
    } = LAYOUT_CONSTANTS[lodashGet(skins, `${skin}.LAYOUT`, 'highlight')]

    return (
        <>
            <div style={{
                fontSize: lodashGet(skins, `${skin}.MONTH_FONT_SIZE`, 'inherit'),
                marginBottom: lodashGet(skins, `${skin}.MONTH_MARGIN_BOTTOM`, 0),
                fontFamily: lodashGet(skins, `${skin}.FONT_FAMILY`, 'inherit'),
            }}>
                {t({ id: 'title' })} {t({ id: month })}
            </div>
            <div
                className={classes.area}
                style={{
                    fontSize: AREA_FONT_SIZE,
                    lineHeight: AREA_LINE_HEIGHT,
                    fontWeight: AREA_FONT_WEIGHT,
                    fontFamily: lodashGet(skins, `${skin}.AREA_FONT_FAMILY`, 'inherit'),
                }}
            >
                {area}
            </div>
        </>
    );
}

export default function LocalMarketShareable(props) {
    const {
        rootClasses,
        parsedData,
        onImagesLoaded,
    } = useShareablesTemplateRoot(props);
    const classes = useStyles(props);
    const month = getMonthName();
    const {
        area,
        median_price,
        median_price_mm,
        active_listings_count,
        active_listings_count_mm,
        median_dom,
        median_dom_mm,
        new_listings_count,
        skin = DEFAULT_SKIN,
        watermark = false,
        language = 'en',
        from_month,
        to_month,
    } = parsedData || {};
    const bgImage = lodashGet(skins, `${skin}.BACKGROUND_PATH`);
    const loadedBgs = useImagesLoaded([bgImage]);
    const { WRAP_PADDING } = LAYOUT_CONSTANTS[lodashGet(skins, `${skin}.LAYOUT`, 'highlight')]
    const formattedMedianPrice = (median_price > 0)
        ? `$${(median_price > 1000000) ? numAbbreviate(median_price, 1000000, 2) : addCommas(median_price)}`
        : '-';

    useEffect(() => {
        if(loadedBgs && !window.isLoaded) {
            if(typeof onImagesLoaded === 'function') {
                onImagesLoaded();
            } else {
                window.isLoaded = true;
            }
        }
    }, [loadedBgs, onImagesLoaded]);

    if(!parsedData) return null;

    const layout = lodashGet(skins, `${skin}.LAYOUT`, DEFAULT_LAYOUT);
    const displayTitleInChildComponent = ['columns', 'highlight'].includes(layout);
    const areaFontFamily = lodashGet(skins, `${skin}.AREA_FONT_FAMILY`, 'inherit');
    const defaultFontFamily = lodashGet(skins, `${skin}.FONT_FAMILY`, 'inherit');

    return (
        <>
            <IntlProvider
                locale={language}
                messages={languages[language]}
                defaultLocale="en"
            >
                <link rel="stylesheet" href="./css/ll-circular-pro.css" />
                {areaFontFamily === 'Caveat' && <link rel="stylesheet" href={CAVEAT} />}
                {defaultFontFamily === 'Courier Prime' && <link rel="stylesheet" href={COURIER_PRIME} />}

                <div className={`${rootClasses.root} ${classes.root}`} id="templateRoot">
                    <div className={rootClasses.viewport}>

                        {/* Main Wrapper */}
                        <div className={classes.wrap}
                            style={{
                                backgroundImage: `url("${bgImage}")`,
                                color: lodashGet(skins, `${skin}.TEXT_COLOR`, COLOR_BLACK),
                                padding: WRAP_PADDING,
                            }}
                        >
                            {!displayTitleInChildComponent && (
                                <Title skin={skin} area={area} month={month} />
                            )}

                            <StatsComponent
                                layout={layout}
                                skin={skin}
                                medianDom={median_dom}
                                medianDomTrend={median_dom_mm}
                                medianPrice={formattedMedianPrice}
                                medianPriceTrend={median_price_mm}
                                activeListingsCount={active_listings_count}
                                activeListingsCountTrend={active_listings_count_mm}
                                newListingsCount={new_listings_count}
                                month={month}
                                area={area}
                                fromMonth={from_month}
                                toMonth={to_month}
                                fontFamily={defaultFontFamily}
                                language={language}
                            />

                            {watermark && (<div className={classes.watermark}>
                                <img src="./images/watermark-stamp.svg" alt="Watermark" />
                            </div>)}
                        </div>
                    </div>
                </div>
            </IntlProvider>
        </>
    );
}
